import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Calendar, Phone, Mail, User, 
  CheckCircle, Clock, XCircle, Trash2, Loader2,
  RefreshCw, Send, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [token]);

  const updateStatus = async (bookingId, newStatus) => {
    setUpdating(bookingId);
    try {
      await axios.put(
        `${API}/bookings/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(bookings.map(b => 
        b.id === bookingId ? { ...b, status: newStatus } : b
      ));
      toast.success(`Booking marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update booking');
    } finally {
      setUpdating(null);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookings.filter(b => b.id !== bookingId));
      toast.success('Booking deleted');
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const resendEmail = async (bookingId) => {
    setUpdating(bookingId);
    try {
      const { data } = await axios.post(
        `${API}/bookings/${bookingId}/resend-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(bookings.map(b => 
        b.id === bookingId
          ? { ...b, email_sent: data.email_sent, email_error: data.email_error }
          : b
      ));
      if (data.email_sent) {
        toast.success('Notification email sent to your inbox');
      } else {
        toast.error(`Email failed: ${data.email_error || 'unknown error'}`);
      }
    } catch (error) {
      toast.error('Could not resend email');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {bookings.length} total booking{bookings.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={fetchBookings}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-800" />
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">No bookings yet</h3>
              <p className="text-gray-500 mt-2">
                When customers submit booking requests, they will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Booking Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <User className="w-5 h-5 text-amber-600" />
                          {booking.name}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          {booking.email_sent === true && (
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 flex items-center gap-1"
                              data-testid={`email-status-sent-${booking.id}`}
                              title="Notification email delivered to your inbox"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Email sent
                            </span>
                          )}
                          {booking.email_sent === false && booking.email_error && (
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1"
                              data-testid={`email-status-failed-${booking.id}`}
                              title={booking.email_error}
                            >
                              <AlertCircle className="w-3 h-3" />
                              Email failed
                            </span>
                          )}
                          {!booking.email_sent && !booking.email_error && (
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 flex items-center gap-1"
                              data-testid={`email-status-unknown-${booking.id}`}
                              title="No email delivery record. Click 'Resend email' to send a fresh notification."
                            >
                              <AlertCircle className="w-3 h-3" />
                              Email status unknown
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${getStatusColor(booking.status)}`}>
                            {getStatusIcon(booking.status)}
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${booking.email}`} className="hover:text-amber-800">
                            {booking.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${booking.phone}`} className="hover:text-amber-800">
                            {booking.phone}
                          </a>
                        </div>
                      </div>
                      
                      <div className="bg-amber-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-amber-900">
                          Service: {booking.service}
                        </p>
                        {booking.message && (
                          <p className="text-sm text-gray-600 mt-1">
                            Message: {booking.message}
                          </p>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-500">
                        Submitted: {formatDate(booking.created_at)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 lg:flex-col">
                      {!booking.email_sent && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500 text-amber-700 hover:bg-amber-50"
                          onClick={() => resendEmail(booking.id)}
                          disabled={updating === booking.id}
                          data-testid={`resend-email-${booking.id}`}
                        >
                          {updating === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Resend email
                            </>
                          )}
                        </Button>
                      )}
                      {booking.status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => updateStatus(booking.id, 'confirmed')}
                          disabled={updating === booking.id}
                        >
                          {updating === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Confirm'
                          )}
                        </Button>
                      )}
                      {booking.status === 'confirmed' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateStatus(booking.id, 'completed')}
                          disabled={updating === booking.id}
                        >
                          {updating === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Complete'
                          )}
                        </Button>
                      )}
                      {(booking.status === 'pending' || booking.status === 'confirmed') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => updateStatus(booking.id, 'cancelled')}
                          disabled={updating === booking.id}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deleteBooking(booking.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBookings;
